<script setup>
import { Head } from '@inertiajs/vue3'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import { destinations as allDestinations, experiences as allExperiences, months } from '@/data/content.js'

defineOptions({ layout: PublicLayout })

// Curated content lives in @/data/content.js so Home stays in sync with the
// Destinations / Journal pages. Home shows a subset.
const destinations = allDestinations.slice(0, 6)
const experiences  = allExperiences.slice(0, 4)

const culture = [
  { label: 'The people', title: 'Hosts, not a spectacle', body: 'Kenya is forty-plus communities — Maasai, Samburu, Swahili, Kikuyu, Luo and more. You meet them as hosts, on their terms.' },
  { label: 'The table',  title: 'Nyama choma & chai',     body: 'Grilled over open fire, simmered in coconut on the coast, shared loudly. Food here is an invitation.' },
  { label: 'The coast',  title: 'A thousand years of trade', body: 'Swahili towns where Africa, Arabia and India met — dhows, carved stone, the call to prayer over the rooftops.' },
]
</script>

<template>
  <Head>
    <title>Kenya2Go — Meet the real Kenya</title>
    <meta name="description" content="Safaris, the Swahili coast, and the living culture of Kenya — planned with local hands. Browse experiences and request to book.">
    <meta property="og:title" content="Kenya2Go — Meet the real Kenya">
    <meta property="og:description" content="Safaris, the Swahili coast, and the living culture of Kenya — planned with local hands.">
    <meta property="og:type" content="website">
  </Head>

  <div class="khome">
    <!-- ░░ HERO ░░ -->
    <section class="hero">
      <div class="hero__scene" aria-hidden="true">
        <div class="hero__sky"></div>
        <div class="hero__sun"></div>
        <div class="hero__haze"></div>
        <div class="hero__hills hero__hills--far"></div>
        <div class="hero__hills hero__hills--mid"></div>
        <div class="hero__hills hero__hills--near"></div>
        <svg class="hero__acacia" viewBox="0 0 220 200" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M108 200V92" stroke="currentColor" stroke-width="6" stroke-linecap="round"/>
          <path d="M108 120c-14-2-30-10-44-26M108 110c16-3 34-12 48-30M108 132c-8-1-18-6-26-14M108 122c10-2 22-8 30-18"
                stroke="currentColor" stroke-width="4" stroke-linecap="round"/>
          <path d="M30 70c20-26 56-30 80-22 22-7 56-2 78 20 12 12 14 22 12 26-40-16-142-16-184 2-3-6 2-18 14-26Z"
                fill="currentColor"/>
        </svg>
        <div class="hero__grain"></div>
      </div>

      <div class="hero__content">
        <p class="eyebrow eyebrow--ondark">Kenya · East Africa</p>
        <h1 class="hero__title">Meet the&nbsp;real Kenya.</h1>
        <p class="hero__lede">
          Safaris that stop you breathing, a coast a thousand years in the making,
          and a culture that meets you as a guest — planned with local hands.
        </p>
        <div class="hero__cta">
          <a href="/tours" class="btn btn--solid">Explore experiences</a>
          <a href="#destinations" class="btn btn--ghost">Where to go</a>
        </div>
        <ul class="hero__chips">
          <li>Safari</li><li>The coast</li><li>Culture</li><li>Adventure</li>
        </ul>
      </div>
    </section>

    <!-- ░░ INTRO / KARIBU ░░ -->
    <section class="intro">
      <p class="intro__kw">Karibu</p>
      <p class="intro__lead">
        Kenya isn’t a checklist of animals. It’s dawn light on the Mara, the smell of
        nyama choma, a dhow’s sail filling off Lamu, a stranger who calls you friend.
        We help you feel it — through experiences run by the people who live it.
      </p>
    </section>

    <!-- ░░ DESTINATIONS ░░ -->
    <section id="destinations" class="section">
      <header class="section__head">
        <div>
          <p class="eyebrow">Where to go</p>
          <h2 class="section__title">From the plains to the sea</h2>
        </div>
        <a href="/destinations" class="section__more">All destinations →</a>
      </header>

      <div class="dest-grid">
        <a v-for="(d, i) in destinations" :key="d.slug" class="dest" :class="`dest--${i}`" :href="`/destinations/${d.slug}`"
           :style="{ '--h': d.hue }">
          <div class="dest__art"></div>
          <div class="dest__body">
            <p class="dest__tag">{{ d.tag }}</p>
            <h3 class="dest__name">{{ d.name }}</h3>
            <p class="dest__blurb">{{ d.blurb }}</p>
          </div>
        </a>
      </div>
    </section>

    <!-- ░░ EXPERIENCES ░░ -->
    <section class="section section--alt">
      <header class="section__head">
        <div>
          <p class="eyebrow">Ways to feel Kenya</p>
          <h2 class="section__title">Experiences, not packages</h2>
        </div>
        <a href="/tours" class="section__more">Browse all →</a>
      </header>

      <div class="exp-grid">
        <a v-for="x in experiences" :key="x.title" class="exp" href="/tours" :style="{ '--h': x.hue }">
          <div class="exp__art"><span class="exp__kind">{{ x.kind }}</span></div>
          <div class="exp__body">
            <h3 class="exp__title">{{ x.title }}</h3>
            <div class="exp__meta">
              <span>{{ x.days }}</span>
              <span class="exp__price">from <strong>${{ x.from }}</strong></span>
            </div>
          </div>
        </a>
      </div>
    </section>

    <!-- ░░ SPIRIT / CULTURE ░░ -->
    <section class="section spirit">
      <header class="section__head section__head--center">
        <div>
          <p class="eyebrow">The spirit of Kenya</p>
          <h2 class="section__title">Felt, not just seen</h2>
        </div>
      </header>
      <div class="spirit-grid">
        <article v-for="c in culture" :key="c.title" class="spirit__card">
          <p class="spirit__label">{{ c.label }}</p>
          <h3 class="spirit__title">{{ c.title }}</h3>
          <p class="spirit__body">{{ c.body }}</p>
        </article>
      </div>
    </section>

    <!-- ░░ WHEN TO GO ░░ -->
    <section class="section section--alt">
      <header class="section__head">
        <div>
          <p class="eyebrow">When to go</p>
          <h2 class="section__title">There’s no wrong season</h2>
        </div>
        <a href="/seasonal" class="section__more">Month by month →</a>
      </header>
      <ul class="cal">
        <li v-for="mo in months" :key="mo.m" class="cal__mo" :class="`cal__mo--${mo.s}`">
          <span class="cal__m">{{ mo.m }}</span>
        </li>
      </ul>
      <ul class="cal-key">
        <li><span class="dot dot--peak"></span> Peak game-viewing · the Migration</li>
        <li><span class="dot dot--dry"></span> Hot &amp; dry, great wildlife</li>
        <li><span class="dot dot--green"></span> Green season, lush &amp; quiet</li>
        <li><span class="dot dot--short"></span> Short rains</li>
      </ul>
    </section>

    <!-- ░░ TRUST ░░ -->
    <section class="section trust">
      <div class="trust__grid">
        <div class="trust__item"><h3>Local operators</h3><p>Every experience is run by vetted Kenyan guides and vendors — your money stays in Kenya.</p></div>
        <div class="trust__item"><h3>A real person</h3><p>Request to book and a human confirms availability and details — by email, WhatsApp or Telegram.</p></div>
        <div class="trust__item"><h3>No surprises</h3><p>Clear inclusions, group sizes and meeting points up front. Ask us anything before you commit.</p></div>
      </div>
    </section>

    <!-- ░░ CTA ░░ -->
    <section class="cta">
      <div class="cta__inner">
        <h2 class="cta__title">Let’s plan your Kenya.</h2>
        <p class="cta__lede">Tell us what moves you — wildlife, the coast, culture, or all of it — and we’ll shape the trip around you.</p>
        <div class="cta__actions">
          <a href="/tours" class="btn btn--solid">Start with an experience</a>
          <a href="/contact" class="btn btn--line">Talk to us</a>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.khome { flex: 1; width: 100%; }

/* shared bits ----------------------------------------------------------- */
.eyebrow {
  font-family: var(--body);
  font-size: 0.72rem; letter-spacing: 0.22em; text-transform: uppercase;
  font-weight: 600; color: var(--accent); margin: 0 0 0.6rem;
}
.eyebrow--ondark { color: oklch(0.86 0.10 70); }

.btn {
  display: inline-flex; align-items: center; justify-content: center;
  padding: 0.85rem 1.4rem; border-radius: 999px; font-weight: 600;
  font-size: 0.95rem; text-decoration: none; transition: transform .15s ease, background .15s ease, border-color .15s ease;
  white-space: nowrap;
}
.btn--solid { background: var(--accent); color: var(--accentInk); }
.btn--solid:hover { transform: translateY(-2px); }
.btn--ghost { color: oklch(0.97 0.02 80); border: 1px solid oklch(0.97 0.02 80 / 0.5); }
.btn--ghost:hover { background: oklch(1 0 0 / 0.1); }
.btn--line { color: var(--ink); border: 1px solid var(--lineStrong); }
.btn--line:hover { border-color: var(--accent); color: var(--accent); }

.section { max-width: 70rem; margin: 0 auto; padding: 5rem 1.5rem; width: 100%; box-sizing: border-box; }
.section--alt { max-width: none; background: var(--surfaceAlt); }
.section--alt > * { max-width: 70rem; margin-inline: auto; }
.section__head { display: flex; align-items: flex-end; justify-content: space-between; gap: 1rem; margin-bottom: 2.5rem; }
.section__head--center { justify-content: center; text-align: center; }
.section__title { font-family: var(--display); font-weight: 500; font-size: clamp(1.7rem, 3.6vw, 2.6rem); line-height: 1.05; letter-spacing: -0.015em; margin: 0; color: var(--ink); }
.section__more { color: var(--inkSoft); text-decoration: none; font-weight: 600; font-size: 0.9rem; white-space: nowrap; }
.section__more:hover { color: var(--accent); }

/* HERO ------------------------------------------------------------------ */
.hero { position: relative; min-height: min(86vh, 760px); display: flex; align-items: flex-end; overflow: hidden; isolation: isolate; }
.hero__scene { position: absolute; inset: 0; z-index: -1; color: oklch(0.20 0.05 40); }
.hero__sky { position: absolute; inset: 0;
  background:
    linear-gradient(180deg,
      oklch(0.36 0.09 250) 0%,
      oklch(0.50 0.11 60) 42%,
      oklch(0.68 0.15 55) 64%,
      oklch(0.80 0.16 68) 82%,
      oklch(0.86 0.13 75) 100%);
}
.hero__sun { position: absolute; left: 64%; bottom: 30%; width: 200px; height: 200px; border-radius: 50%;
  background: radial-gradient(circle, oklch(0.96 0.10 85) 0%, oklch(0.90 0.16 78) 45%, transparent 70%);
  filter: blur(2px); }
.hero__haze { position: absolute; inset: 0; background: radial-gradient(ellipse 120% 60% at 64% 70%, oklch(0.88 0.13 75 / 0.55), transparent 60%); }
.hero__hills { position: absolute; left: 0; right: 0; bottom: 0; }
.hero__hills--far  { height: 34%; background: oklch(0.42 0.07 45 / 0.55); clip-path: polygon(0 60%, 18% 44%, 38% 56%, 60% 38%, 80% 52%, 100% 40%, 100% 100%, 0 100%); }
.hero__hills--mid  { height: 26%; background: oklch(0.33 0.06 40 / 0.8);  clip-path: polygon(0 52%, 24% 64%, 50% 44%, 72% 60%, 100% 48%, 100% 100%, 0 100%); }
.hero__hills--near { height: 17%; background: oklch(0.20 0.05 38); clip-path: polygon(0 60%, 30% 70%, 55% 56%, 78% 68%, 100% 58%, 100% 100%, 0 100%); }
.hero__acacia { position: absolute; right: 7%; bottom: 14%; width: clamp(140px, 18vw, 230px); height: auto; color: oklch(0.16 0.04 35); }
.hero__grain { position: absolute; inset: 0; opacity: 0.10; mix-blend-mode: overlay;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"); }

.hero__content { position: relative; max-width: 70rem; width: 100%; margin: 0 auto; padding: 0 1.5rem 4.5rem; }
.hero__title { font-family: var(--display); color: oklch(0.99 0.02 85); font-weight: 400;
  font-size: clamp(2.8rem, 8vw, 5.4rem); line-height: 0.98; letter-spacing: -0.02em; margin: 0 0 1.1rem; max-width: 14ch;
  text-shadow: 0 2px 30px oklch(0.2 0.05 40 / 0.45); }
.hero__lede { color: oklch(0.97 0.02 82); font-size: clamp(1rem, 1.5vw, 1.2rem); line-height: 1.55; max-width: 36rem; margin: 0 0 1.8rem; text-shadow: 0 1px 16px oklch(0.2 0.05 40 / 0.4); }
.hero__cta { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 1.8rem; }
.hero__chips { list-style: none; display: flex; flex-wrap: wrap; gap: 0.5rem; padding: 0; margin: 0; }
.hero__chips li { font-size: 0.8rem; font-weight: 600; color: oklch(0.97 0.02 82); padding: 0.35rem 0.8rem; border: 1px solid oklch(0.97 0.02 82 / 0.35); border-radius: 999px; backdrop-filter: blur(4px); }

/* INTRO ----------------------------------------------------------------- */
.intro { max-width: 52rem; margin: 0 auto; padding: 5rem 1.5rem 3rem; text-align: center; }
.intro__kw { font-family: var(--display); font-style: italic; font-size: 1.3rem; color: var(--accent); margin: 0 0 1rem; }
.intro__lead { font-family: var(--display); font-weight: 400; font-size: clamp(1.4rem, 3vw, 2.1rem); line-height: 1.32; letter-spacing: -0.01em; color: var(--ink); margin: 0; }

/* DESTINATIONS ---------------------------------------------------------- */
.dest-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.1rem; }
.dest { position: relative; display: flex; flex-direction: column; border-radius: var(--radius-lg); overflow: hidden; text-decoration: none; background: var(--surface); border: 1px solid var(--line); transition: transform .2s ease, box-shadow .2s ease; min-height: 280px; }
.dest:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); }
.dest__art { height: 170px; position: relative;
  background:
    radial-gradient(120% 90% at 20% 0%, oklch(0.9 0.11 calc(var(--h))) 0%, transparent 55%),
    linear-gradient(160deg, oklch(0.66 0.15 calc(var(--h))) 0%, oklch(0.45 0.12 calc(var(--h) + 20)) 100%); }
.dest__art::after { content: ''; position: absolute; inset: 0; background: linear-gradient(180deg, transparent 40%, oklch(0.2 0.04 40 / 0.25)); }
.dest--0 { grid-column: span 2; }
.dest--0 .dest__art { height: 220px; }
.dest__body { padding: 1.1rem 1.2rem 1.3rem; }
.dest__tag { font-size: 0.72rem; letter-spacing: 0.12em; text-transform: uppercase; color: var(--accent); font-weight: 600; margin: 0 0 0.3rem; }
.dest__name { font-family: var(--display); font-weight: 500; font-size: 1.5rem; line-height: 1.05; margin: 0 0 0.4rem; color: var(--ink); }
.dest__blurb { font-size: 0.9rem; line-height: 1.5; color: var(--inkSoft); margin: 0; }

/* EXPERIENCES ----------------------------------------------------------- */
.exp-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1.1rem; }
.exp { display: flex; flex-direction: column; border-radius: var(--radius-lg); overflow: hidden; background: var(--surface); border: 1px solid var(--line); text-decoration: none; transition: transform .2s ease, box-shadow .2s ease; }
.exp:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); }
.exp__art { height: 130px; position: relative; display: flex; align-items: flex-start; padding: 0.7rem;
  background: linear-gradient(155deg, oklch(0.7 0.14 calc(var(--h))) 0%, oklch(0.46 0.11 calc(var(--h) + 15)) 100%); }
.exp__kind { font-size: 0.68rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: oklch(0.99 0.02 85); background: oklch(0.2 0.04 40 / 0.28); padding: 0.25rem 0.6rem; border-radius: 999px; }
.exp__body { padding: 0.9rem 1rem 1.1rem; }
.exp__title { font-family: var(--display); font-weight: 500; font-size: 1.12rem; line-height: 1.12; margin: 0 0 0.6rem; color: var(--ink); }
.exp__meta { display: flex; align-items: baseline; justify-content: space-between; font-size: 0.82rem; color: var(--muted); }
.exp__price strong { color: var(--ink); font-size: 0.95rem; }

/* SPIRIT ---------------------------------------------------------------- */
.spirit-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem; }
.spirit__card { padding: 1.8rem 1.6rem; border-radius: var(--radius-lg); background: var(--surface); border: 1px solid var(--line); position: relative; }
.spirit__card::before { content: ''; position: absolute; left: 1.6rem; top: 1.8rem; width: 2rem; height: 2px; background: var(--accent); }
.spirit__label { margin: 1.2rem 0 0.7rem; font-size: 0.72rem; letter-spacing: 0.14em; text-transform: uppercase; color: var(--muted); font-weight: 600; }
.spirit__title { font-family: var(--display); font-weight: 500; font-size: 1.45rem; line-height: 1.1; margin: 0 0 0.7rem; color: var(--ink); }
.spirit__body { font-size: 0.95rem; line-height: 1.6; color: var(--inkSoft); margin: 0; }

/* WHEN TO GO ------------------------------------------------------------ */
.cal { list-style: none; display: grid; grid-template-columns: repeat(12, 1fr); gap: 0.4rem; padding: 0; margin: 0 0 1.4rem; }
.cal__mo { aspect-ratio: 1 / 1.4; border-radius: var(--radius-md); display: flex; align-items: flex-end; justify-content: center; padding-bottom: 0.5rem; font-size: 0.78rem; font-weight: 600; color: oklch(0.25 0.04 50); }
.cal__mo--peak  { background: oklch(0.66 0.15 45); color: oklch(0.99 0.02 85); }
.cal__mo--dry   { background: oklch(0.84 0.13 78); }
.cal__mo--green { background: oklch(0.74 0.11 150); }
.cal__mo--short { background: oklch(0.80 0.07 200); }
.cal-key { list-style: none; display: flex; flex-wrap: wrap; gap: 1.2rem; padding: 0; margin: 0; font-size: 0.85rem; color: var(--inkSoft); }
.cal-key li { display: inline-flex; align-items: center; gap: 0.5rem; }
.dot { width: 0.7rem; height: 0.7rem; border-radius: 999px; }
.dot--peak { background: oklch(0.66 0.15 45); }
.dot--dry { background: oklch(0.84 0.13 78); }
.dot--green { background: oklch(0.74 0.11 150); }
.dot--short { background: oklch(0.80 0.07 200); }

/* TRUST ----------------------------------------------------------------- */
.trust__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; }
.trust__item h3 { font-family: var(--display); font-weight: 500; font-size: 1.2rem; margin: 0 0 0.5rem; color: var(--ink); }
.trust__item p { font-size: 0.92rem; line-height: 1.6; color: var(--inkSoft); margin: 0; }

/* CTA ------------------------------------------------------------------- */
.cta { background: linear-gradient(135deg, oklch(0.42 0.10 45) 0%, oklch(0.32 0.07 40) 100%); color: oklch(0.98 0.02 85); }
.cta__inner { max-width: 48rem; margin: 0 auto; padding: 5rem 1.5rem; text-align: center; }
.cta__title { font-family: var(--display); font-weight: 400; font-size: clamp(2rem, 5vw, 3.2rem); line-height: 1.02; letter-spacing: -0.02em; margin: 0 0 1rem; }
.cta__lede { font-size: 1.05rem; line-height: 1.6; color: oklch(0.94 0.03 82); max-width: 34rem; margin: 0 auto 2rem; }
.cta__actions { display: flex; flex-wrap: wrap; gap: 0.75rem; justify-content: center; }
.cta .btn--line { color: oklch(0.98 0.02 85); border-color: oklch(0.98 0.02 85 / 0.45); }
.cta .btn--line:hover { border-color: oklch(0.98 0.02 85); color: oklch(0.98 0.02 85); background: oklch(1 0 0 / 0.08); }

/* RESPONSIVE ------------------------------------------------------------ */
@media (max-width: 860px) {
  .dest-grid { grid-template-columns: repeat(2, 1fr); }
  .dest--0 { grid-column: span 2; }
  .exp-grid { grid-template-columns: repeat(2, 1fr); }
  .spirit-grid, .trust__grid { grid-template-columns: 1fr; gap: 1rem; }
}
@media (max-width: 540px) {
  .dest-grid { grid-template-columns: 1fr; }
  .dest--0 { grid-column: span 1; }
  .exp-grid { grid-template-columns: 1fr; }
  .cal { grid-template-columns: repeat(6, 1fr); }
  .section { padding: 3.5rem 1.25rem; }
}
</style>
